import { AuthenticatedTemplate, UnauthenticatedTemplate, useMsal } from '@azure/msal-react';
import { initializeIcons } from '@fluentui/react/lib/Icons';
import { FluentProvider, Image, makeStyles, shorthands, tokens } from '@fluentui/react-components';
import { useEffect, useState } from 'react';
import { AppContent } from 'layout/components/AppContent';
import { Loading } from 'layout/components/views/Loading';
import { Login } from 'layout/components/views/Login';
import { useChat } from 'modules/core/hooks/useChat';
import { useCollection } from 'modules/core/hooks/useCollection';
import { usePersona } from 'modules/core/hooks/usePersona';
import { useServiceInfo } from 'modules/core/hooks/useServiceInfo';
import { useAuth } from 'shared/hooks/useAuth';
import { useTheme } from 'shared/hooks/useTheme';
import { useAppDispatch, useAppSelector } from 'shared/redux/app/hooks';
import { RootState } from 'shared/redux/app/store';
import { setActiveUserInfo, setAdminSettings, setServiceInfo } from 'shared/redux/features/app/appSlice';
import { ActiveUserInfo } from 'shared/redux/features/app/AppState';
import { TokenHelper } from 'shared/utils/TokenUtils';

export const useClasses = makeStyles({
    container: {
        display: 'flex',
        flexDirection: 'column',
        height: '100vh',
        width: '100vw',
        overflow: 'hidden',
    },
    header: {
        alignItems: 'center',
        backgroundColor: tokens.colorBrandForeground2,
        color: tokens.colorNeutralForegroundOnBrand,
        display: 'flex',
        height: '48px',
        justifyContent: 'space-between',
        ...shorthands.padding(tokens.spacingVerticalNone, tokens.spacingHorizontalXL),
        boxShadow: '0 3px 5px rgba(57, 63, 72, 0.3)',
        zIndex: 9999,
    },
    headerTitle: {
        display: 'flex',
        alignItems: 'center',
    },
    persona: {
        marginRight: tokens.spacingHorizontalXXL,
    },
    cornerItems: {
        display: 'flex',
        alignItems: 'center',
        gap: tokens.spacingHorizontalXXXL,
    },
    headerLogo: {
        marginLeft: '10px',
    },
    environmentTag: {
        marginLeft: '20px',
    },
});

export enum AppState {
    ProbeForBackend,
    SettingUserInfo,
    ErrorLoadingChats,
    ErrorLoadingCollections,
    ErrorLoadingPersonas,
    ErrorLoadingUserInfo,
    ErrorLoadingServiceInfo,
    LoadingServiceInfo,
    LoadingChats,
    LoadingCollections,
    LoadingPersonas,
    Chat,
    Collections,
    Personas,
    SigningOut,
    AdminSettings,
    UserUnauthorized,
    Skill,
}

const App = () => {
    const [appState, setAppState] = useState(AppState.ProbeForBackend);

    const classes = useClasses();
    const dispatch = useAppDispatch();
    // TODO: Put this back in if you want an automatic redirect
    // useMsalAuthentication(InteractionType.Redirect);

    const { isAuthenticated, isAuthEnabled } = useAuth();
    const { logo, theme } = useTheme();
    const { instance, inProgress } = useMsal();
    const { isMaintenance } = useAppSelector((state: RootState) => state.app);
    const { selectedId } = useAppSelector((state: RootState) => state.collections);
    const { loadChats } = useChat();
    const { loadCollections } = useCollection();
    const { loadPersonas } = usePersona();
    const { getServiceInfo, getAdminSettings } = useServiceInfo();

    async function setUserProfilePhoto(activeUserInfo: ActiveUserInfo) {
        const accessToken = await TokenHelper.getGraphApiAccessTokenUsingMsal(instance);
        if (!accessToken) {
            return;
        }

        const graphResponse = await fetch('https://graph.microsoft.com/beta/me/photo/$value', {
            headers: { Authorization: `Bearer ${accessToken}` },
        });

        const pictureBlob = await graphResponse.blob();

        dispatch(
            setActiveUserInfo({
                ...activeUserInfo,
                photoUrl: URL.createObjectURL(pictureBlob),
            }),
        );
    }

    useEffect(() => {
        initializeIcons();
        void getAdminSettings()
            .then((result) => {
                dispatch(setAdminSettings(result));
            })
            .catch(() => {
                console.error('Failed to load admin settings');
            });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (isMaintenance && appState !== AppState.ProbeForBackend) {
            setAppState(AppState.ProbeForBackend);
            return;
        }

        if (isAuthenticated && appState === AppState.SettingUserInfo) {
            const account = instance.getActiveAccount();
            if (!account) {
                setAppState(AppState.ErrorLoadingUserInfo);
            } else {
                const activeUserInfo: ActiveUserInfo = {
                    id: `${account.localAccountId}.${account.tenantId}`,
                    email: account.username, // username is the email address
                    username: account.name ?? account.username,
                    photoUrl: undefined,
                };

                dispatch(setActiveUserInfo(activeUserInfo));

                void Promise.resolve(setUserProfilePhoto(activeUserInfo));

                setAppState(AppState.LoadingServiceInfo);
            }
        }

        // No auth alternative, set a default user
        if (!isAuthenticated && !isAuthEnabled && appState === AppState.SettingUserInfo) {
            dispatch(
                setActiveUserInfo({
                    id: 'id',
                    email: 'default@email.com', // username is the email address
                    username: 'Default',
                    photoUrl: undefined,
                }),
            );

            setAppState(AppState.LoadingServiceInfo);
        }

        if ((isAuthenticated || !isAuthEnabled) && appState === AppState.LoadingServiceInfo) {
            void Promise.all([
                // Load service information
                getServiceInfo()
                    .then((serviceInfo) => {
                        dispatch(setServiceInfo(serviceInfo));
                        setAppState(AppState.LoadingChats);
                    })
                    .catch((e: Error) => {
                        if (e.message.includes('Unauthorized')) {
                            setAppState(AppState.UserUnauthorized);
                        } else {
                            setAppState(AppState.ErrorLoadingServiceInfo);
                        }
                    }),
            ]);
        }

        if ((isAuthenticated || !isAuthEnabled) && appState === AppState.LoadingChats) {
            void Promise.all([
                // Load all chats into memory
                loadChats()
                    .then(() => {
                        setAppState(AppState.Chat);
                    })
                    .catch(() => {
                        setAppState(AppState.ErrorLoadingChats);
                    }),

                // TODO: Make this more efficient
                // Load all collections into memory
                loadCollections().catch(() => {
                    setAppState(AppState.ErrorLoadingCollections);
                }),

                // Load all available personas into memory
                loadPersonas().catch(() => {
                    setAppState(AppState.ErrorLoadingPersonas);
                }),
            ]);
        }

        if ((isAuthenticated || !isAuthEnabled) && appState === AppState.LoadingCollections) {
            if (selectedId) {
                setAppState(AppState.Collections);
            } else {
                void Promise.all([
                    // Load all collections into memory
                    loadCollections()
                        .then(() => {
                            setAppState(AppState.Collections);
                        })
                        .catch(() => {
                            setAppState(AppState.ErrorLoadingCollections);
                        }),
                ]);
            }
        }

        if ((isAuthenticated || !isAuthEnabled) && appState === AppState.LoadingPersonas) {
            void Promise.all([
                // Load all personas into memory
                loadPersonas()
                    .then(() => {
                        setAppState(AppState.Personas);
                    })
                    .catch(() => {
                        setAppState(AppState.ErrorLoadingPersonas);
                    }),
            ]);
        }

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [instance, inProgress, isAuthenticated, appState, isMaintenance]);

    const content = <AppContent classes={classes} appState={appState} setAppState={setAppState} />;

    return (
        <FluentProvider className="app-container" theme={theme ?? undefined}>
            {isAuthEnabled ? (
                <>
                    <UnauthenticatedTemplate>
                        <div className={classes.container}>
                            <div className={classes.header}>
                                <div className={classes.headerTitle}>
                                    <Image src={logo} alt="[Arinco Logo]" height={34} />
                                </div>
                            </div>

                            {appState === AppState.SigningOut && <Loading text="Signing you out..." />}

                            {appState !== AppState.SigningOut && <Login />}
                        </div>
                    </UnauthenticatedTemplate>

                    <AuthenticatedTemplate>{content}</AuthenticatedTemplate>
                </>
            ) : (
                content
            )}
        </FluentProvider>
    );
};

export default App;
